// Import auth service for authenticated requests
import { authService } from './authService';
import { getServerURL } from './config';

// Generation Stories Service for CRUD operations
const SERVER_URL = getServerURL();

export const generationStoriesService = {
  // Test API connectivity
  async testConnection() {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      return false;
    }
    
    try {
      console.log('Testing connection to:', `${SERVER_URL}/generation-stories/`);
      const response = await fetch(`${SERVER_URL}/generation-stories/`, {
        method: 'GET',
        headers: authService.getAuthHeaders(),
      });
      console.log('Connection test response status:', response.status);
      return response.ok;
    } catch (error) {
      console.error('Connection test failed:', error);
      return false;
    }
  },

  // Get all generation stories
  async getAllStories() {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    
    try {
      const response = await fetch(`${SERVER_URL}/generation-stories/`, {
        headers: authService.getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        // Return only the stories array
        return Array.isArray(data.data) ? data.data : [];
      } else {
        console.error('API Error:', response.status, response.statusText);
        throw new Error(`Failed to fetch generation stories: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching generation stories:', error);
      // Return mock data for development when API is unavailable
      return [];
    }
  },

  // Get single generation story by ID
  async getStoryById(id) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    
    try {
      const response = await fetch(`${SERVER_URL}/generation-stories/${id}/`, {
        headers: authService.getAuthHeaders(),
      });
      if (response.ok) {
        return await response.json();
      } else {
        throw new Error(`Failed to fetch generation story: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching generation story:', error);
      throw error;
    }
  },

  // Create new generation story
  async createStory(storyData) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    
    try {
      console.log('Sending request to:', `${SERVER_URL}/generation-stories/`);
      console.log('Request data:', storyData);
      
      const response = await fetch(`${SERVER_URL}/generation-stories/`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(storyData),
      });
      
      console.log('Response status:', response.status);
      console.log('Response status text:', response.statusText);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (response.ok) {
        const result = await response.json();
        console.log('Success response:', result);
        return result;
      } else {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          errorData = { message: 'Failed to parse error response' };
        }
        console.error('Error response:', errorData);
        throw new Error(`Failed to create generation story: ${response.status} - ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Network error creating generation story:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to the server. Please check your internet connection.');
      }
      throw error;
    }
  },

  // Update generation story
  async updateStory(id, storyData) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    
    try {
      const response = await fetch(`${SERVER_URL}/generation-stories/${id}/`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(storyData),
      });
      if (response.ok) {
        return await response.json();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Failed to update generation story: ${response.status} - ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating generation story:', error);
      throw error;
    }
  },

  async updateTextScenes(id, payload) {
    if (!SERVER_URL) {
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/generation-stories/${id}/text-scenes`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(payload || {}),
      });
      if (response.ok) {
        return await response.json();
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Failed to update story text scenes: ${response.status} - ${errorData.error || errorData.message || 'Unknown error'}`
      );
    } catch (error) {
      console.error('Error updating story text scenes:', error);
      throw error;
    }
  },

  async getTextScenes(id) {
    if (!SERVER_URL) {
      throw new Error('API server URL is not configured');
    }

    const response = await fetch(`${SERVER_URL}/generation-stories/${id}/text-scenes`, {
      headers: authService.getAuthHeaders(),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || `Failed to fetch story text scenes: ${response.status}`);
    }
    return Array.isArray(data.data) ? data.data : [];
  },

  // Delete generation story
  async deleteStory(id) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    
    try {
      console.log('Sending delete request to:', `${SERVER_URL}/generation-stories/${id}/`);
      console.log('Deleting generation story with ID:', id);
      
      const response = await fetch(`${SERVER_URL}/generation-stories/${id}/`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({
          text: `Generation story with ID ${id}`
        }),
      });
      
      console.log('Delete response status:', response.status);
      console.log('Delete response status text:', response.statusText);
      
      if (response.ok) {
        const result = await response.json();
        console.log('Delete success response:', result);
        return result;
      } else {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (parseError) {
          console.error('Failed to parse delete error response:', parseError);
          errorData = { message: 'Failed to parse error response' };
        }
        console.error('Delete error response:', errorData);
        throw new Error(`Failed to delete generation story: ${response.status} - ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Network error deleting generation story:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to the server. Please check your internet connection.');
      }
      throw error;
    }
  },

  // Duplicate generation story
  async duplicateStory(id, duplicateData) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    
    try {
      const response = await fetch(`${SERVER_URL}/generation-stories/${id}/duplicate/`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(duplicateData),
      });
      if (response.ok) {
        return await response.json();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Failed to duplicate generation story: ${response.status} - ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error duplicating generation story:', error);
      throw error;
    }
  },

  // Get deleted generation stories
  async getDeletedStories() {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    
    try {
      const response = await fetch(`${SERVER_URL}/generation-stories/deleted/`, {
        headers: authService.getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        return Array.isArray(data.data) ? data.data : [];
      } else {
        console.error('API Error:', response.status, response.statusText);
        throw new Error(`Failed to fetch deleted generation stories: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching deleted generation stories:', error);
      return [];
    }
  },

  // Restore generation story
  async restoreStory(id) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }
    
    try {
      const response = await fetch(`${SERVER_URL}/generation-stories/${id}/restore/`, {
        method: 'PATCH',
        headers: authService.getAuthHeaders(),
      });
      if (response.ok) {
        return await response.json();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Failed to restore generation story: ${response.status} - ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error restoring generation story:', error);
      throw error;
    }
  },
}; 